import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Button, 
  Card, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Tooltip, 
  makeStyles, 
  withStyles, 
  lighten, 
  darken,
} 
from '@material-ui/core';
import InfoIconOutlined from '@material-ui/icons/InfoOutlined';
import NumberFormat from 'react-number-format';
import ProductModalList from '../Common/ProductModalList';
import './TermQuoteCard.scss';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AppTypeDigitalIcon from '../../../../icons/appType-Digital.svg';
import AppTypePhoneIcon from '../../../../icons/appType-Phone.svg';
import StructureLevelIcon from '../../../../icons/structure-level.svg';
import StructureDecreasingIcon from '../../../../icons/structure-decreasing.svg';
import MedicalExamYesIcon from '../../../../icons/medicalExam-yes.svg';
import ConvertibilityConvertibleIcon from '../../../../icons/convertibility-convertible.svg';

const TooltipColor = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
    cursor: 'pointer'
  },
}))(Tooltip);

const useStyles = makeStyles((theme) => ({
  textPrimary: {
    color: theme.palette.primary.main
  },
  lightBg: {
    backgroundColor: `${theme.palette.primary.main}16`,
  },
  lightBackground: {
    backgroundColor: lighten(theme.palette.primary.main, 0.98),
  },
  tagBg: {
    color: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.main}26`,
  },
  priceColor: {
    fontSize: "30px",
    fontWeight: "bold",
    letterSpacing: "-0.16px",
    '&::before': {
      content: '"$"',
      fontSize: "12px",
      verticalAlign: "super",
    }
  },
  whiteDollar: {
    '&::before': {
      color: '#fff',
    }
  },
  primaryDollar: {
    '&::before': {
      color: theme.palette.primary.main,
    }
  },
  priceText: {
    fontSize: "12px"
  },
  featuredBackground: {
    backgroundColor: lighten(theme.palette.primary.main, 0.92),
  },
  featuredText: {
    color: "#ffffff",
  },
  featuredButton: {
    backgroundColor: "#ffffff",
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: lighten(theme.palette.primary.main, 0.9),
      color: theme.palette.primary.main,
    }
  },
  selectButton: {
    fontWeight: 700,
    letterSpacing: "1.25px",
    color: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.main}16`,
    boxShadow: "0 2px 3px rgba(0,0,0,0.19)",
    border: `1px solid ${theme.palette.primary.main}`,
    '&:hover': {
      backgroundColor: lighten(theme.palette.primary.main, 0.9),
      color: theme.palette.primary.main,
    },
    "&.accordion-expanded": {
      color: "#ffffff",
      backgroundColor: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: darken(theme.palette.primary.main, 0.2),
      }
    }
  },
}));

export default function TermQuoteCard(props) {
  const classes = useStyles();
  const productDetailObj = props.quote;
  const productCharacteristics = [
    {
      title: "Phone Application",
      iconName: <AppTypePhoneIcon className={classes.textPrimary} />,
      description: "An over-the-phone life insurance application is a process where individuals apply for a life insurance policy by speaking with an insurance agent or representative over the phone. During the conversation, the applicant provides personal and medical information, answers questions about their health and lifestyle, and discusses their insurance needs. The agent typically fills out the application on the applicant's behalf, and the applicant may need to provide additional documentation or go through a medical underwriting process, depending on the policy's requirements. This approach offers a convenient way to apply for life insurance without the need for in-person meetings, though it may involve a detailed discussion and review of the applicant's information.",
      shouldShow: productDetailObj.requiresPhoneInterview,
    },
    {
      title: "Medical Exam",
      iconName: <MedicalExamYesIcon className={classes.textPrimary} />,
      description: "A life insurance medical exam, also known as a life insurance paramedical exam, is a standard procedure often required by life insurance companies when individuals apply for a life insurance policy, especially for larger coverage amounts. During the exam, a qualified healthcare professional, such as a nurse or medical technician, conducts a series of tests and collects health-related information from the applicant. These tests typically include:<ol><li>Blood pressure measurement.</li><li>Blood and urine samples to check for various health markers like cholesterol levels, blood sugar, and the presence of drugs or nicotine.</li><li>A medical history review, which includes questions about past illnesses, family medical history, and lifestyle choices, such as smoking or alcohol consumption.</li></ol>The results of the medical exam help the insurance company assess the applicant's overall health and mortality risk, which in turn determines the premium rates and coverage eligibility. While not all life insurance policies require a medical exam, they are more common for larger coverage amounts or when the applicant has certain health risks. No-exam life insurance policies are also available, but they often come with higher premiums to offset the insurer's risk.",
      shouldShow: (productDetailObj.requiresMedicalExam && productDetailObj.requiresMedicalExam.toLowerCase() !== 'no'),
    },
    {
      title: "Digital Application",
      iconName: <AppTypeDigitalIcon className={classes.textPrimary} />,
      description: "Instead of traditional paper applications, applicants can complete and submit all the necessary information and documentation through a website, mobile app, or other digital platforms provided by insurance companies. These digital applications typically allow users to enter personal and medical information, select coverage options, and even undergo electronic underwriting, often speeding up the approval process. It offers a more convenient and efficient way for individuals to secure life insurance coverage.",
      shouldShow: productDetailObj.isDigitalProduct,
    },
    {
      title: "Level Term",
      iconName: <StructureLevelIcon className={classes.textPrimary} />,
      description: "Level term insurance is a type of life insurance policy that offers a fixed death benefit and consistent premiums over a specified period, usually between 10 to 30 years. If the insured person passes away during this term, the policy pays out the predetermined death benefit to beneficiaries. However, if the insured person outlives the term, the policy expires without any payout. Level term insurance is an affordable and straightforward way to provide financial protection to loved ones during specific vulnerable periods, such as while paying off a mortgage or supporting dependents. It doesn't build cash value and is known for its predictability.",
      shouldShow: !productDetailObj.isDecreasingTerm,
    },
    {
      title: "Decreasing Term",
      iconName: <StructureDecreasingIcon className={classes.textPrimary} />,
      description: "Decreasing term insurance is a type of life insurance policy where the death benefit gradually decreases over time, typically in sync with a specific financial obligation like a mortgage or a loan. The premiums remain constant, but the coverage amount decreases as the policy matures. This insurance is designed to provide financial protection for a specific debt or expense, ensuring that if the insured person passes away, the policy can help pay off the outstanding balance, making it an appropriate choice for individuals looking to cover a diminishing financial liability.",
      shouldShow: productDetailObj.isDecreasingTerm,
    },
    {
      title: "Convertible",
      iconName: <ConvertibilityConvertibleIcon className={classes.textPrimary} />,
      description: `<ul>${productDetailObj.convertibility}</ul>`,
      shouldShow: productDetailObj.hasConvertibility
    },
  ];

  const [accordionExpanded, setAccordionExpanded] = useState(false);

  return (
    <div className={`term-quote-card ${props.isFeatured ? classes.featuredBackground : 'light-bg'}`}>
      <Accordion onChange={(obj,card) => setAccordionExpanded(card)} square>
        <AccordionSummary className={classes.accordionButton} expandIcon={<ExpandMoreIcon />}>
          <div className="product-summary">
            <div className="logo-container">
              <img className="carrier-logo-lg" src={props.logoUrl} alt={`${props.carrierName}`}/>
            </div>
            <div className="ratings-container">
              <h4>Carrier Rating</h4>
              <TooltipColor title="">
                <span className={`carrier-rating ${classes.textPrimary}`}>{props.rating}&nbsp;Rating&nbsp;<InfoIconOutlined /></span>
              </TooltipColor>
            </div>
            <div className="time-container">
              <h4>Approval Time</h4>
              <p>{productDetailObj.underwritingTimeTypical}</p>
            </div>
            <div className="product-note-container">
              <ul dangerouslySetInnerHTML={{ __html: props.quote.productValueProposition }} />
            </div>
            <div className="rate-container">
              <p className={`price-container`}>
                <NumberFormat
                  className={`${classes.priceColor} ${classes.textPrimary} ${classes.primaryDollar}`}
                  value={props.monthlyRate}
                  displayType={'text'}
                  thousandSeparator={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                />
                <span
                  className={`${classes.priceText} ${classes.textPrimary}`}> /mo</span>
              </p>
            </div>
            <div className="action-container">
              <Button
                      className={classes.selectButton + (accordionExpanded ? " accordion-expanded" : "")}
                      color="primary"
                      variant={accordionExpanded ? "contained" : "outlined"} onClick={props.onClick} aria-describedby={props.productName}
                      id={props.productName}>
                Select
              </Button>
            </div>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <div className="product-details-grid">
            <div className="details-header-container">
              <h2>{productDetailObj.productName} &mdash; {productDetailObj.termLength} Year Term</h2>
            </div>
            <div className="details-content-container">
              <div className="mobile-only-info-container">
                <div className="ratings-container">
                  <h4>Carrier Rating</h4>
                  <TooltipColor title="">
                    <span className={`carrier-rating ${classes.textPrimary}`}>{props.rating}&nbsp;Rating&nbsp;<InfoIconOutlined /></span>
                  </TooltipColor>
                </div>
                <div className="time-container">
                  <h4>Approval Time</h4>
                  <p>{productDetailObj.underwritingTimeTypical}</p>
                </div>
                <div className="product-note-container">
                  <ul dangerouslySetInnerHTML={{ __html: props.quote.productValueProposition }} />
                </div>
              </div>
              <div className="product-info-container">
                <h3>Product Information</h3>
                <div className="modal-list-wrapper">
                  <ProductModalList data={productCharacteristics} />
                </div>
                <div className="product-info-details">
                  <p dangerouslySetInnerHTML={{ __html: props.productDetails.props.productDescription }} />
                  <ul dangerouslySetInnerHTML={{ __html: props.productDetails.props.productInformation }} />
                </div>      
              </div>
              <div className="riders-container">
                <h3>Policy Riders</h3>
                {productDetailObj.availableRiders && <p>Some riders are available at no additional cost, some will affect your price.</p>}
                <div className="modal-list-wrapper">
                  {productDetailObj.availableRiders ? <ProductModalList data={productDetailObj.availableRiders} /> : <div className="modal-list-container"><p>No riders are available for this product.</p></div>}
                </div>
              </div>
              <div className="ratings-container">
                <h3>Insurance Carrier Ratings</h3>
                <p>These ratings are performed by independent governing bodies and reflect the reliability of {props.carrierName} as a carrier.</p>
                <div className="rating-cards-wrapper">
                  <ul className="rating-cards">
                    {productDetailObj.ratingAMBest && (
                      <li>
                          <ProductDetailsRatingCard logo="/logos/ratings/ambest.jpg" rater="AM Best" rating={productDetailObj.ratingAMBest}/>
                      </li>
                    )}
                    {productDetailObj.ratingSP && (
                      <li>
                          <ProductDetailsRatingCard logo="/logos/ratings/sp.jpg" rater="S&amp;P Global" rating={productDetailObj.ratingSP}/>
                      </li>
                    )}
                    {productDetailObj.ratingMoodys && (
                      <li>
                          <ProductDetailsRatingCard logo="/logos/ratings/moodys.jpg" rater="Moody's" rating={productDetailObj.ratingMoodys}/>
                      </li>
                    )}
                    {productDetailObj.ratingFitch && (
                      <li>
                        <ProductDetailsRatingCard logo="/logos/ratings/fitch.jpg" rater="Fitch" rating={productDetailObj.ratingFitch}/>
                      </li>
                    )}
                    {productDetailObj.ratingComdex && (
                      <li>
                          <ProductDetailsRatingCard logo="/logos/ratings/comdex.jpg" rater="Comdex" rating={productDetailObj.ratingComdex}/>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
            <div className="mobile-cta-container">
              <Button color="primary"
                    variant="contained" onClick={props.onClick} aria-describedby={props.productName}
                    id={props.productName}>
                Select Product
              </Button>
            </div>
            <div className="details-footer-container">
              {productDetailObj.disclosureDetail && (
              <Fragment>
                <h3>Disclosure</h3>
                <p>{productDetailObj.disclosureDetail}</p>
              </Fragment>
              )}
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}

const ProductDetailsRatingCard = (props) => {
  return (
    <Card className="rating-card">
      <div className="rating-card-head">
        <img src={props.logo} alt={props.rater} className="img-fluid"/>
      </div>
      <p className="rater">{props.rater}</p>
      <p className="rating">{props.rating}</p>
    </Card>
  );
};

TermQuoteCard.propTypes = {
  carrierName: PropTypes.string.isRequired,
  monthlyRate: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
  expand: PropTypes.func.isRequired,
  productDetails: PropTypes.object.isRequired,
  buttonText: PropTypes.string,
  isSortedByPrice: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./src/components/Quoters/CustomizeTerm/QuoterComponents/TermQuoteCard.js